import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isBefore, isToday, startOfDay } from 'date-fns';
import { CalendarClock, ClipboardList, ChevronRight } from 'lucide-react';
import { PilotCrmService } from '@/services/pilot-crm.service';
import { useAuth } from '@/contexts/AuthContext';
import { Badge, Button, Card, LoadingSpinner, SectionHeader } from '@/components/common';
import { EmployerLead, LeadTask } from '@/models/pilot-crm.model';

const isPilotCrmEditor = (role?: string | null) => {
  if (!role) return false;
  const normalized = role === 'operationsAdmin' ? 'operations_admin' : role;
  return ['operations_admin', 'executive', 'support'].includes(normalized);
};

export const PilotCrmTasks: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  const canEdit = isPilotCrmEditor(profile?.role);

  const { data: tasks = [], isLoading: tasksLoading } = useQuery({
    queryKey: ['pilot-crm-tasks'],
    queryFn: () => PilotCrmService.getTasks(),
    enabled: !!profile,
  });

  const { data: leads = [], isLoading: leadsLoading } = useQuery({
    queryKey: ['pilot-crm-leads'],
    queryFn: () => PilotCrmService.getLeads(),
    enabled: !!profile,
  });

  const leadById = useMemo(() => {
    const map = new Map<string, EmployerLead>();
    leads.forEach((lead) => map.set(lead.id, lead));
    return map;
  }, [leads]);

  const openTasks = tasks.filter((task) => task.status === 'open');
  const today = startOfDay(new Date());

  const grouped = useMemo(() => {
    const overdue: LeadTask[] = [];
    const todayTasks: LeadTask[] = [];
    const upcoming: LeadTask[] = [];

    openTasks.forEach((task) => {
      if (isBefore(task.dueAt, today) && !isToday(task.dueAt)) {
        overdue.push(task);
      } else if (isToday(task.dueAt)) {
        todayTasks.push(task);
      } else {
        upcoming.push(task);
      }
    });

    return { overdue, today: todayTasks, upcoming };
  }, [openTasks, today]);

  const updateTaskMutation = useMutation({
    mutationFn: async ({ taskId }: { taskId: string }) => {
      if (!profile?.id) return;
      await PilotCrmService.updateTask(taskId, { status: 'done' }, profile.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pilot-crm-tasks'] });
    },
  });

  if (tasksLoading || leadsLoading) {
    return <LoadingSpinner text="Loading tasks..." fullScreen />;
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <SectionHeader
        icon={CalendarClock}
        title="Pilot CRM Tasks"
        description="Open tasks grouped by due date"
      />

      <div className="space-y-6">
        <TaskGroup
          title="Overdue"
          tasks={grouped.overdue}
          leadById={leadById}
          onOpenLead={(leadId) => navigate(`/pilot-crm/leads/${leadId}`)}
          onMarkDone={(taskId) => updateTaskMutation.mutate({ taskId })}
          canEdit={canEdit}
          badgeVariant="danger"
        />
        <TaskGroup
          title="Today"
          tasks={grouped.today}
          leadById={leadById}
          onOpenLead={(leadId) => navigate(`/pilot-crm/leads/${leadId}`)}
          onMarkDone={(taskId) => updateTaskMutation.mutate({ taskId })}
          canEdit={canEdit}
          badgeVariant="warning"
        />
        <TaskGroup
          title="Upcoming"
          tasks={grouped.upcoming}
          leadById={leadById}
          onOpenLead={(leadId) => navigate(`/pilot-crm/leads/${leadId}`)}
          onMarkDone={(taskId) => updateTaskMutation.mutate({ taskId })}
          canEdit={canEdit}
          badgeVariant="info"
        />
      </div>
    </div>
  );
};

const TaskGroup: React.FC<{
  title: string;
  tasks: LeadTask[];
  leadById: Map<string, EmployerLead>;
  onOpenLead: (leadId: string) => void;
  onMarkDone: (taskId: string) => void;
  canEdit: boolean;
  badgeVariant: 'info' | 'warning' | 'danger';
}> = ({ title, tasks, leadById, onOpenLead, onMarkDone, canEdit, badgeVariant }) => {
  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ClipboardList className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        </div>
        <Badge variant={badgeVariant} size="sm">
          {tasks.length}
        </Badge>
      </div>
      {tasks.length === 0 ? (
        <p className="text-sm text-gray-500">No tasks.</p>
      ) : (
        <div className="space-y-3">
          {tasks.map((task) => {
            const lead = leadById.get(task.leadId);
            return (
              <div
                key={task.id}
                className="flex flex-wrap items-center justify-between gap-3 border border-gray-200 rounded-lg p-4"
              >
                <div>
                  <p className="font-semibold text-gray-900">{task.title}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {lead?.companyName || 'Unknown lead'}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => onOpenLead(task.leadId)}>
                    View lead
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                  {canEdit && (
                    <Button variant="primary" size="sm" onClick={() => onMarkDone(task.id)}>
                      Mark done
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
};
